/** @jsxImportSource @emotion/react */

import { Pages } from 'modules/navigation/domain/enums/pages';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIsAdmin } from 'shared/hooks/use-is-admin';

import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import NotFound from 'ud-ui/components/images/not-found.png';
import UDText from 'ud-ui/components/text';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { fetchBoardsByTemplateId, fetchCatalogTemplates } from 'modules/template-editor/store/templates/actions';
import { selectCatalogTemplatesByFilter } from 'modules/template-editor/store/templates/selectors';
import Header from '../components/catalog/header';
import NewTemplate from '../components/catalog/new-template';
import TemplateItem from '../components/catalog/template-item';
import { TemplateListCatalog } from './styles';
import { theme } from 'styles/theme/theme';

function Catalog() {
  const { setHeaderContent } = useMainLayout();
  const breadcrumb = useMemo(
    () => (
      <UDBreadcrumbs
        className="my-10"
        breadcrumbs={[
          { label: 'Трансляции', to: Pages.TEMPLATE.INDEX },
          { label: 'Готовые шаблоны', to: Pages.TEMPLATE.CATALOG.INDEX },
        ]}
      />
    ),
    [],
  );

  const dispatchWithToast = useDispatchWithToast();
  const templates = useSelector(selectCatalogTemplatesByFilter);
  const { loadedTemplates } = useTemplates();

  const isAdmin = useIsAdmin();

  useEffect(() => {
    if (templates.length > 0) {
      return;
    }
    dispatchWithToast(fetchCatalogTemplates({ visibility: isAdmin ? ['superAdmin', 'all'] : ['all'] }), {
      // Пустой ответ раньше тоже считался сбоем — сохраняем это поведение.
      success: (catalogTemplates) => (catalogTemplates ? undefined : 'Не удалось загрузить шаблоны каталога'),
      error: 'Не удалось загрузить шаблоны каталога',
    });
  }, [dispatchWithToast, isAdmin, templates.length]);

  useEffect(() => {
    if (templates.length > 0) {
      const promises = templates
        .filter((template) => !loadedTemplates.includes(template.id))
        .map((template) =>
          dispatchWithToast(fetchBoardsByTemplateId({ templateId: template.id }), { error: 'Не удалось загрузить табло' }),
        );
      Promise.all(promises);
    }
  }, [dispatchWithToast, loadedTemplates, templates, templates.length]);

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [breadcrumb, setHeaderContent]);

  return (
    <>
      <div className={'flex flex-col'} style={{ height: '100%', overflow: 'hidden' }}>
        <Header />
        {templates.length > 0 ? (
          <TemplateListCatalog>
            {templates.map((template) => (
              <TemplateItem key={template.id} template={template} />
            ))}
            {isAdmin && <NewTemplate />}
          </TemplateListCatalog>
        ) : (
          <div className={'flex-center flex h-full w-full flex-col'}>
            <img src={NotFound} alt={'empty'} style={{ height: 130, width: 130 }} />
            <UDText type={'callout'} style={{ color: theme.colors.SurfaceRomanSilver30 }}>
              По такому запросу ничего не найдено
            </UDText>
          </div>
        )}
      </div>
    </>
  );
}

export default Catalog;
